import React from "react";
import cn from "classnames";
import { Button, Icon, Modal } from "@telia/teddy";
import "./InfoModal.less";

interface InfoModalProps {
    ariaLabelledBy: string;
    ariaDescribedBy?: string;
    open: boolean;
    closeButtonText?: string;
    setOpen: (open: boolean) => void;
    size?: "small" | "medium" | "large" | "fullscreen";
    className?: string;
    children?: React.ReactNode;
}

interface InfoModalBodyProps {
    noPadding?: boolean;
    children?: React.ReactNode;
}

interface InfoModalHeaderProps {
    headerText?: string;
    ingress?: string;
    backgroundColor?: string;
}

const getModalSize = (size: InfoModalProps["size"]) => {
    if (size === "small") return "sm";
    if (size === "large" || size === "fullscreen") return "lg";
    return "md";
};

export const InfoModal: React.FC<InfoModalProps> = ({
    ariaLabelledBy,
    ariaDescribedBy,
    open,
    closeButtonText,
    setOpen,
    size,
    className,
    children,
}) => (
    <Modal open={open} onOpenChange={(nextOpen) => !nextOpen && setOpen(false)}>
        <Modal.Overlay className="info-modal">
            <Modal.Content
                aria-labelledby={ariaLabelledBy}
                aria-describedby={ariaDescribedBy}
                className={cn("info-modal__container", className)}
                size={getModalSize(size)}
            >
                <Modal.Close slot="content">
                    <Button
                        className="info-modal__close-button"
                        variant="tertiary-purple"
                        iconOnly
                        aria-label={closeButtonText || "Lukk"}
                    >
                        <Icon name="close" aria-hidden="true" focusable="false" />
                    </Button>
                </Modal.Close>
                {children}
            </Modal.Content>
        </Modal.Overlay>
    </Modal>
);

export const InfoModalHeader: React.FC<InfoModalHeaderProps> = ({ headerText, ingress, backgroundColor }) => (
    <Modal.Group
        className="info-modal__heading"
        style={backgroundColor ? { backgroundColor } : undefined}
        variant="title"
    >
        <Modal.Title className="info-modal__title" as="h2">
            {headerText}
        </Modal.Title>
        {ingress && <Modal.Description className="info-modal__description">{ingress}</Modal.Description>}
    </Modal.Group>
);

export const InfoModalBody: React.FC<InfoModalBodyProps> = ({ noPadding, children }) => (
    <div className={cn("info-modal__content", { "info-modal__content--no-padding": noPadding })}>{children}</div>
);
